import { connectPreview, connectStorageScope } from "./base_path";

export function trackingIntegrations() {
  try {
    const node = document.getElementById("ssc-tracking-config");
    const parsed = node ? JSON.parse(node.textContent || "[]") : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

function conversionKey(eventId) {
  return `ssc_conversion:${connectStorageScope()}:${eventId}`;
}

// Request-derived ids (`req-…`) dedupe only within this window: an immediate
// retry/double-submit must count once, but an identical genuine submission
// later in the session must count again.
const REQUEST_DEDUP_WINDOW_MS = 10 * 60 * 1000;

function alreadyTracked(eventId) {
  if (!eventId) return false;
  try {
    const key = conversionKey(eventId);
    const stored = window.sessionStorage.getItem(key);
    if (stored) {
      if (!String(eventId).startsWith("req-")) return true;
      const at = Number(stored);
      if (Number.isFinite(at) && Date.now() - at < REQUEST_DEDUP_WINDOW_MS) {
        return true;
      }
    }
    window.sessionStorage.setItem(key, String(Date.now()));
  } catch (_error) {
    // Storage can be unavailable in private mode. Tracking still proceeds.
  }
  return false;
}

export function newConversionId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_error) {
    // Fall through to a per-tab timestamp/random id.
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Stable id for an AJAX lead submission, derived from the request path + body:
// a retry or double-submit of the same logical submission reuses the id, so
// alreadyTracked() reports the conversion once, while a genuinely different
// submission hashes to a new id. Falls back to a random id when the body can't
// be serialized (then dedup is skipped, matching the pre-hash behavior).
export function conversionIdForRequest(input, init) {
  const body = serializeRequestBody(init?.body);
  if (body === null) return newConversionId();
  try {
    const raw = typeof input === "string" ? input : input.url;
    const path = new URL(raw, window.location.origin).pathname;
    return `req-${fnv1a(`${path}?${body}`)}`;
  } catch (_error) {
    return newConversionId();
  }
}

function serializeRequestBody(body) {
  try {
    if (typeof body === "string") return body;
    if (body instanceof URLSearchParams) return body.toString();
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      const pairs = [];
      body.forEach((value, key) => {
        const serialized =
          typeof value === "string"
            ? value
            : `file:${value.name || ""}:${value.size || 0}`;
        pairs.push(`${key}=${serialized}`);
      });
      return pairs.join("&");
    }
  } catch (_error) {
    // Fall through — treat as unserializable.
  }
  return null;
}

function fnv1a(str) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

// Canonical browser conversion event for Connect. Structured providers use it,
// and advanced raw snippets can subscribe to `connect:lead` on window.
export function trackAdvertisingLead(
  source = "lead",
  eventId = newConversionId(),
) {
  if (alreadyTracked(eventId)) return false;

  try {
    window.dispatchEvent(
      new CustomEvent("connect:lead", {
        detail: { source, event_id: eventId },
      }),
    );
  } catch (_error) {
    // A third-party listener must never break Connect.
  }

  const integrations = trackingIntegrations().filter(
    (item) => item.enabled && item.track_lead,
  );
  integrations.forEach((item) => {
    try {
      if (item.type === "meta" && typeof window.fbq === "function") {
        window.fbq(
          "trackSingle",
          item.pixel_id,
          "Lead",
          { content_name: source },
          { eventID: eventId },
        );
      } else if (
        item.type === "google_ads" &&
        typeof window.gtag === "function"
      ) {
        window.gtag("event", "conversion", {
          send_to: `${item.conversion_id}/${item.conversion_label}`,
          // Google Ads dedupes repeated conversions by transaction_id.
          transaction_id: eventId,
        });
      } else if (item.type === "ga4" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          send_to: item.measurement_id,
          lead_source: source,
          // GA4 has no automatic dedup key for generate_lead; carrying the
          // stable event id lets downstream reporting dedupe rare double-fires
          // (e.g. two tabs racing the cookie-session one-shot claim).
          transaction_id: eventId,
        });
      }
    } catch (_error) {
      // Provider failures are isolated from the visitor flow.
    }
  });

  if (integrations.some((item) => item.type === "gtm")) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "connect_lead",
        connect_source: source,
        event_id: eventId,
      });
    } catch (_error) {
      // Best effort.
    }
  }
  return true;
}

// Fire the ad conversion the server parked for this browser, as returned by
// the page-view beacon (one-shot server-side — see Connect::PublicSession
// claim_connect_conversion!). The redirect flows (no-JS lead form,
// registration confirm/verify) hand off through the session instead of a URL
// param, so conversions can't be forged with `?lead_event=` or re-fired from a
// bookmarked/shared post-conversion URL.
export async function trackClaimedConversion(response) {
  try {
    if (!response?.ok || response.status === 204) return;
    const data = await response.json();
    const conversion = data?.conversion;
    if (!conversion?.event_id) return;
    const source = conversion.type === "registration" ? "registration" : "lead";
    trackAdvertisingLead(source, String(conversion.event_id));
  } catch (_error) {
    // Beacon responses are best-effort; tracking must never break the page.
  }
}

// A successful registration POST. The code-verify endpoint is used by flows
// that complete IN PLACE (AVM gate, pre-approval) with no follow-up page load,
// so any parked conversion must be claimed immediately — the next page-view
// beacon may never come. Returning logins hit the same endpoint but park
// nothing, so the claim probe is a harmless 204 for them.
export function successfulRegistrationRequest(input, init, response) {
  return requestMatches(input, init, response, [
    "/auth/connect",
    "/auth/connect/code",
  ]);
}

// Claim-only beacon: fetch any parked conversion without recording another
// page view (Connect::PageViewsController skips the enqueue for claim_only).
// Goes through window.fetch so the path-preview prefix rewrite still applies.
export function claimConversionNow() {
  // Same reason the page-view beacon bails (connect.js#trackConnectPageView):
  // in the editor preview /_track is served from the CRM host, where it sits
  // behind the Connect subdomain constraint and 404s. An agent registering
  // against their own draft isn't a conversion either way.
  if (connectPreview()) return undefined;

  try {
    return window
      .fetch("/_track", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ claim_only: "1" }).toString(),
        credentials: "include",
      })
      .then(trackClaimedConversion)
      .catch(() => {});
  } catch (_error) {
    return undefined;
  }
}

// NOTE: a 2xx from /_lead is not proof a lead persisted — the endpoint
// deliberately answers success-shaped for honeypot hits and some failure
// branches (anti-enumeration), so those disguised responses count here too.
export function successfulLeadRequest(input, init, response) {
  return requestMatches(input, init, response, ["/_lead"]);
}

function requestMatches(input, init, response, pathSuffixes) {
  if (!response?.ok) return false;
  const method = String(
    init?.method || (typeof input !== "string" && input?.method) || "GET",
  ).toUpperCase();
  if (method !== "POST") return false;
  try {
    const raw = typeof input === "string" ? input : input.url;
    const pathname = new URL(raw, window.location.origin).pathname;
    return pathSuffixes.some((suffix) => pathname.endsWith(suffix));
  } catch (_error) {
    return false;
  }
}
